/**
 * Load and drive the WASM solver kernel in the browser.
 *
 * The kernel is a plain `WebAssembly.instantiate` with a handful of integer
 * exports — no wasm-bindgen, no glue codegen. Two assets are needed:
 *
 *   kernel.wasm        the solver (~746KB)
 *   kernel_dataset.bin the packed item data (~600KB)
 *
 * Both are content-addressed by the blob digest in their URL, so a new item
 * dataset invalidates the HTTP cache by changing the path rather than by any
 * cache-busting logic here.
 */

/** Where the assets live, relative to the site root. */
const KERNEL_BASE = '/kernel';

/** Result buffer handed to the kernel; a solve result is a few KB of JSON. */
const RESULT_CAPACITY = 1 << 16;

/**
 * Buffer for the generation report, which is far larger than a solve result:
 * every slot's candidate list and every set-swap combo, each with a score and a
 * probability. Measured at ~20KB on a full request; sized well past that
 * because overflowing it costs the whole report rather than truncating it.
 */
const REPORT_CAPACITY = 1 << 19;

/**
 * Buffer for a slice of tournament estimates: ~200 bytes of JSON per weapon,
 * and an island may be handed a few hundred of them at once.
 */
const ESTIMATE_CAPACITY = 1 << 20;

/** Error codes returned by `dofgen_wasm_solve`. */
const ERR = {
    '-1': 'kernel blob not loaded',
    '-2': 'request was not valid UTF-8',
    '-3': 'solve failed',
    '-4': 'result did not fit in the output buffer',
};

let assetsPromise = null;

/** The kernel's only import, reached solely on a Rust panic. */
const IMPORTS = {
    env: {
        // Make it loud rather than a silent trap.
        abort: () => {
            throw new Error('kernel aborted');
        },
    },
};

/**
 * Fetch the assets and compile the module once, for every island to share.
 *
 * A `WebAssembly.Module` is structured-cloneable, so it can be handed to each
 * worker through `postMessage` and V8 reuses the compiled code rather than
 * recompiling. Letting each worker compile its own copy showed up directly in
 * the numbers: preparing sat at ~1000ms for one, two and four islands and then
 * doubled to ~1980ms at eight, which is eight compilations of the same 790KB
 * competing for cores.
 *
 * @param {string} digest hex digest identifying the dataset build
 * @returns {Promise<{module: WebAssembly.Module, dataset: ArrayBuffer}>}
 */
export function fetchKernelAssets(digest, build) {
    if (assetsPromise) return assetsPromise;

    assetsPromise = (async () => {
        // `build` is the wasm's own content hash. Without it a solver fix that
        // leaves the dataset untouched reuses the old URL and caches never
        // refetch. Older manifests have no build field, so fall back.
        const wasmUrl = build
            ? `${KERNEL_BASE}/kernel.${digest}.${build}.wasm`
            : `${KERNEL_BASE}/kernel.${digest}.wasm`;
        const blobUrl = `${KERNEL_BASE}/dataset.${digest}.bin`;

        const [wasmBuf, dataset] = await Promise.all([
            fetch(wasmUrl).then((r) => {
                if (!r.ok) throw new Error(`kernel fetch ${r.status}`);
                return r.arrayBuffer();
            }),
            fetch(blobUrl).then((r) => {
                if (!r.ok) throw new Error(`dataset fetch ${r.status}`);
                return r.arrayBuffer();
            }),
        ]);

        return { module: await WebAssembly.compile(wasmBuf), dataset };
    })().catch((err) => {
        // Let the next caller retry rather than caching the failure forever.
        assetsPromise = null;
        throw err;
    });

    return assetsPromise;
}

/**
 * Instantiate a shared module and load the item blob into this instance.
 *
 * Each island still needs its own linear memory and its own parsed tables —
 * only the compiled code is shared.
 */
export async function instantiateKernel({ module, dataset }) {
    // Given a Module rather than bytes, this resolves to the Instance itself.
    const instance = await WebAssembly.instantiate(module, IMPORTS);
    const ex = instance.exports;

    const blobPtr = ex.dofgen_wasm_alloc(dataset.byteLength);
    new Uint8Array(ex.memory.buffer).set(new Uint8Array(dataset), blobPtr);
    const nItems = ex.dofgen_wasm_load_blob(blobPtr, dataset.byteLength);
    if (nItems < 0) throw new Error(`dataset rejected by kernel (${nItems})`);

    return { exports: ex, nItems };
}

/**
 * Run one solve.
 *
 * @param {object} kernel result of `loadKernel`
 * @param {object} request shape produced by `toKernelRequest`
 * @param {{ evals?: number, seed?: number }} opts
 * @returns {object} the kernel's result: items, hard, soft, invested, …
 */
export function solveWithKernel(kernel, request, { evals = 1_000_000, seed = 1 } = {}) {
    const ex = kernel.exports;
    const bytes = new TextEncoder().encode(JSON.stringify(request));
    const reqPtr = ex.dofgen_wasm_alloc(bytes.length);
    const outPtr = ex.dofgen_wasm_alloc(RESULT_CAPACITY);

    // The view must be taken after every alloc: growing linear memory detaches
    // any ArrayBuffer taken before it.
    new Uint8Array(ex.memory.buffer).set(bytes, reqPtr);

    const n = ex.dofgen_wasm_solve(
        reqPtr,
        bytes.length,
        BigInt(evals),
        BigInt(seed),
        outPtr,
        RESULT_CAPACITY,
    );
    const mem = new Uint8Array(ex.memory.buffer);

    if (n < 0) {
        const detail = new TextDecoder()
            .decode(mem.subarray(outPtr, outPtr + 512))
            .replace(/\0[\s\S]*$/, '')
            .trim();
        const base = ERR[String(n)] || `kernel error ${n}`;
        throw new Error(detail ? `${base}: ${detail}` : base);
    }

    return JSON.parse(new TextDecoder().decode(mem.subarray(outPtr, outPtr + Number(n))));
}

/** Slots for the 16 item ids handed to the kernel as a search start. */
const N_SLOTS = 16;

/**
 * Do the request-shaped precompute once, keeping it for later `searchChunk`s.
 *
 * Returns the number of non-empty candidate pools. Throws on failure, so a
 * worker reports the reason rather than a bare code.
 */
export function prepareCase(kernel, request, { seed = 1 } = {}) {
    const ex = kernel.exports;
    const bytes = new TextEncoder().encode(JSON.stringify(request));
    const reqPtr = ex.dofgen_wasm_alloc(bytes.length);
    const errPtr = ex.dofgen_wasm_alloc(RESULT_CAPACITY);
    new Uint8Array(ex.memory.buffer).set(bytes, reqPtr);

    const n = ex.dofgen_wasm_prepare(reqPtr, bytes.length, BigInt(seed), errPtr, RESULT_CAPACITY);
    if (n < 0) {
        const mem = new Uint8Array(ex.memory.buffer);
        const detail = new TextDecoder()
            .decode(mem.subarray(errPtr, errPtr + 512))
            .replace(/\0[\s\S]*$/, '')
            .trim();
        const base = PREPARE_ERR[String(n)] || `prepare error ${n}`;
        throw new Error(detail ? `${base}: ${detail}` : base);
    }
    return n;
}

/** Error codes returned by `dofgen_wasm_prepare`. */
const PREPARE_ERR = {
    '-1': 'kernel blob not loaded',
    '-2': 'request was not valid UTF-8',
    '-3': 'prepare failed',
};

/**
 * Search the prepared case for `evals` more evaluations.
 *
 * `start` is 16 item ids to continue from — how an island adopts another's
 * elite. Omit it to continue from the case's own seed.
 */
export function searchChunk(kernel, { evals, seed, start = null }) {
    const ex = kernel.exports;
    let startPtr = 0;
    if (Array.isArray(start) && start.length === N_SLOTS) {
        startPtr = ex.dofgen_wasm_alloc(N_SLOTS * 4);
        // After every alloc, or a grown memory leaves this view detached.
        new Int32Array(ex.memory.buffer, startPtr, N_SLOTS).set(start.map(Number));
    }
    const outPtr = ex.dofgen_wasm_alloc(RESULT_CAPACITY);
    const n = ex.dofgen_wasm_search(
        BigInt(evals), BigInt(seed), startPtr, outPtr, RESULT_CAPACITY,
    );
    return readResult(ex, n, outPtr, 'search');
}

/**
 * How many (weapon, forging) candidates the prepared case's find-the-best-weapon
 * tournament has. 0 means the request has none and the pool searches as usual.
 */
export function armCount(kernel) {
    const ex = kernel.exports;
    return typeof ex.dofgen_wasm_arm_count === 'function' ? ex.dofgen_wasm_arm_count() : 0;
}

/**
 * How many tournament candidates, from the front, are worth estimating: all of
 * them, unless the request carried the potential table, which orders them and
 * keeps the most promising.
 */
export function armShortlist(kernel) {
    const ex = kernel.exports;
    return typeof ex.dofgen_wasm_arm_shortlist === 'function' ? ex.dofgen_wasm_arm_shortlist() : armCount(kernel);
}

/**
 * Estimate tournament candidates `from..to`: a quick build per weapon, which is
 * what decides which of them get searched.
 *
 * @returns {Array<{arm: number, weapon: number, choice: number, hard: number,
 *   soft: number, untargeted: number, items: number[]}>}
 */
export function estimateArms(kernel, from, to) {
    const ex = kernel.exports;
    const outPtr = ex.dofgen_wasm_alloc(ESTIMATE_CAPACITY);
    const n = ex.dofgen_wasm_estimate(from, to, outPtr, ESTIMATE_CAPACITY);
    return readResult(ex, n, outPtr, 'estimate');
}

/**
 * Search one tournament candidate for `evals` from `start` — its best build so
 * far, which carries its weapon. Same result shape as `searchChunk`.
 */
export function searchArm(kernel, { arm, evals, seed, start }) {
    const ex = kernel.exports;
    const startPtr = ex.dofgen_wasm_alloc(N_SLOTS * 4);
    new Int32Array(ex.memory.buffer, startPtr, N_SLOTS).set(start.map(Number));
    const outPtr = ex.dofgen_wasm_alloc(RESULT_CAPACITY);
    const n = ex.dofgen_wasm_search_arm(
        arm, BigInt(evals), BigInt(seed), startPtr, outPtr, RESULT_CAPACITY,
    );
    return readResult(ex, n, outPtr, 'search');
}

/**
 * What the prepared case will search over: top items per slot and top set-swap
 * combos, each with the precompute score and the probability of being drawn.
 *
 * Only worth asking one island — every island prepares the same pools — so the
 * pool calls this once rather than folding it into the `prepare` reply.
 * Returns null rather than throwing: the report is decoration, and losing it
 * must never cost a generation.
 */
export function caseReport(kernel) {
    const ex = kernel.exports;
    const outPtr = ex.dofgen_wasm_alloc(REPORT_CAPACITY);
    const n = ex.dofgen_wasm_report(outPtr, REPORT_CAPACITY);
    if (n < 0) return null;
    try {
        return readResult(ex, n, outPtr, 'report');
    } catch (_err) {
        return null;
    }
}

/** Re-allocate characteristic points over `items` and return the final build. */
export function finalizeBuild(kernel, items, evals) {
    const ex = kernel.exports;
    const itemsPtr = ex.dofgen_wasm_alloc(N_SLOTS * 4);
    new Int32Array(ex.memory.buffer, itemsPtr, N_SLOTS).set(items.map(Number));
    const outPtr = ex.dofgen_wasm_alloc(RESULT_CAPACITY);
    const n = ex.dofgen_wasm_finalize(itemsPtr, BigInt(evals), outPtr, RESULT_CAPACITY);
    return readResult(ex, n, outPtr, 'finalize');
}

/** Decode a JSON result written into linear memory, or throw with the reason. */
function readResult(ex, n, outPtr, what) {
    const mem = new Uint8Array(ex.memory.buffer);
    if (n < 0) {
        const detail = new TextDecoder()
            .decode(mem.subarray(outPtr, outPtr + 512))
            .replace(/\0[\s\S]*$/, '')
            .trim();
        const base = ERR[String(n)] || `${what} error ${n}`;
        throw new Error(detail ? `${base}: ${detail}` : base);
    }
    return JSON.parse(new TextDecoder().decode(mem.subarray(outPtr, outPtr + Number(n))));
}

/** Reset the cached assets (tests, or after a dataset change). */
export function resetKernel() {
    assetsPromise = null;
}
